import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import {
  IMAGE_CONSTANT,
  PAYMENT_INTERVAL,
  TEXT_CONSTANT,
  VIDEO_CONSTANT,
} from '../../../constants/app';
import { formatCurrency, getPercentageSaved } from '../../../utils/helpers';
import { setSelectedPlan } from '../../../store/action/products';
import { setItem } from '../../../utils/cache';
import { FT_IMAGES } from './assets';
import {
  CtaButton,
  Pill,
  PhoneMockup,
  useCenteredScroll,
  useHorizontalWheelScroll,
  useReasonsLineProgress,
  useTrialCta,
} from './shared';

const APP_STORE_URL = 'https://apps.apple.com/in/app/jen/id1601368941';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.mobilitymethod.jen';

const sectionProps = {
  onStart: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

/* ------------------------------------------------------------------ Hero */

export const Hero = ({ onStart, loading }) => (
  <section className="ftl-hero">
    {/* <img className="ftl-hero__photo" src={FT_IMAGES.heroPhoto} alt="" /> */}
    <div className="ftl-hero__fade" />
    <div className="ftl-hero__content">
      <div className="ftl-hero__copy">
        <div className="ftl-hero__heading">
          <Pill>8 reasons it&apos;s finally your year to</Pill>
          <h1 className="ftl-hero__title">
            Move better. Hurt less. And <em>understand why.</em>
          </h1>
        </div>
        <p className="ftl-text-xl ftl-muted">
          Mobility and pain-relief programs built by doctors of physical therapy
          — 15 minutes a day, guided step by step, in your pocket.
        </p>
      </div>

      <div className="ftl-hero__proof">
        <div className="ftl-hero__stores">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src={FT_IMAGES.badgeAppStore}
              alt="Download on the App Store"
              width="144"
              height="48"
            />
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src={FT_IMAGES.badgeGooglePlay}
              alt="Get it on Google Play"
              width="144"
              height="48"
            />
          </a>
        </div>
        <div className="ftl-hero__rating">
          <span className="ftl-hero__stars" aria-label="Rated 4.9 out of 5">
            {[0, 1, 2, 3, 4].map(i => (
              <img key={i} src={FT_IMAGES.star} alt="" width="16" height="16" />
            ))}
          </span>
          <span className="ftl-text-lg ftl-muted">4.9 from 478+ reviews</span>
        </div>
      </div>

      <div className="ftl-hero__cta">
        <CtaButton
          label="Start My Free Trial"
          onStart={() => onStart('Hero')}
          loading={loading}
          withArrow
          className="ftl-btn--glow ftl-btn--block"
        />
        <span className="ftl-text-lg">Cancel any time.</span>
      </div>
    </div>
    <PhoneMockup
      className="ftl-hero__phone"
      screen={FT_IMAGES.heroPhoneScreen}
      alt="Jen Health app — Therapy Plans"
    />
  </section>
);

Hero.propTypes = sectionProps;

/* ------------------------------------------------------- What is Jen Health */

const BENEFITS = [
  {
    icon: FT_IMAGES.iconPain,
    title: 'Say bye to chronic or persistent pain',
    text: 'Follow a real, evolving plan with guided videos that shows you exactly what to do, explain why it works and progresses with you as your body changes."',
  },
  {
    icon: FT_IMAGES.iconSavings,
    title: 'Save thousands. No more waiting',
    text: 'Cut annual healthcare spend on visits you no longer need to book or wait for. Guidance the moment you need it from your phone, tablet or TV.',
  },
  {
    icon: FT_IMAGES.iconKettlebell,
    title: 'Stop avoiding or fearing certain exercises.',
    text: 'Guidance the moment you need it, not weeks after you book.',
  },
  {
    icon: FT_IMAGES.iconTogether,
    title: 'Do it together',
    text: 'Meet with the Dr. Jen and the community monthly for feedback, support & guidance',
  },
];

export const WhatIsJen = () => (
  <section className="ftl-section ftl-what">
    <div className="ftl-container ftl-what__row">
      <div className="ftl-what__main">
        <div className="ftl-what__intro">
          <div className="ftl-stack-8">
            <span className="ftl-eyebrow">What is Jen Health</span>
            <h2 className="ftl-h1">
              Guided therapy &amp; exercise that actually fits into your life
            </h2>
          </div>
          <p className="ftl-text-xl ftl-muted ftl-what__lead">
            You never have to guess which exercise comes next or why it matters.
            Jen Health is your video-based educational platform, build by Dr.
            Jen Fraboni, PT, DPT for the moment you decide you&apos;re done
            living around your pain.
          </p>
        </div>
        <div className="ftl-what__grid">
          {BENEFITS.map(item => (
            <div className="ftl-benefit" key={item.title}>
              <img src={item.icon} alt="" width="80" height="80" />
              <div className="ftl-stack-16">
                <h3 className="ftl-h4">{item.title}</h3>
                <p className="ftl-text-lg ftl-faint">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="ftl-photo-card ftl-what__photo">
        <picture>
          <source
            media="(max-width: 767px)"
            srcSet={FT_IMAGES.strengthPhotoMobile}
          />
          <img
            className="ftl-photo-card__img"
            src={FT_IMAGES.strengthPhoto}
            alt="Dr. Jen in a split squat holding a dumbbell"
          />
        </picture>
        <div className="ftl-photo-card__caption">
          <h3 className="ftl-h2 text-white">Strength that protects you</h3>
          <p className="ftl-text-xl text-white">
            Progressive loading so the relief holds up in real life.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------- Free week timeline + plans */

const TIMELINE = [
  {
    day: 'TODAY',
    title: 'Free access starts',
    text: 'Start a Therapy Plan, watch a webinar or search through over 350+ videos to find what your body needs',
  },
  {
    day: 'DAY 5',
    title: 'We remind you',
    text: 'An email or notification before anything is charged.',
  },
  {
    day: 'DAY 7',
    title: 'Membership begins',
    text: 'Start a guided Jen Health Roadmap, lock in your rate & cancel anytime.',
    highlight: true,
  },
];

const money = (plan, perMonth, fallback) => {
  const cents = Number(plan?.attributes?.amount_in_cents);
  if (!cents) return fallback;
  return formatCurrency(
    perMonth ? cents / 100 / 12 : cents / 100,
    plan.attributes.currency,
  );
};

const PricingPlans = ({ onStart, loading }) => {
  const dispatch = useDispatch();
  const { products, selectedPlan } = useSelector(state => state.product);
  const { isDisabled } = useTrialCta(loading);
  const yearly = products?.find(
    p => p?.attributes?.interval === PAYMENT_INTERVAL.YEARLY,
  );
  const monthly = products?.find(
    p => p?.attributes?.interval === PAYMENT_INTERVAL.MONTHLY,
  );
  const savedValue = Number(getPercentageSaved(products));
  // Fall back to the design copy when products haven't loaded yet; hide the
  // badge if the configured prices don't actually give a yearly saving.
  const saved = products?.length ? savedValue : 40;
  const hasSaving = Number.isFinite(saved) && saved > 0;
  const selectedInterval =
    selectedPlan?.attributes?.interval || PAYMENT_INTERVAL.YEARLY;

  const plans = [
    {
      interval: PAYMENT_INTERVAL.YEARLY,
      plan: yearly,
      label: 'Yearly',
      price: money(yearly, true, '$14.99'),
      featuredNote: `${money(
        yearly,
        false,
        '$179.99',
      )} paid yearly after trial`,
      compactNote: hasSaving
        ? `Save ${saved}% · ${money(yearly, false, '$179.99')} billed yearly`
        : `${money(yearly, false, '$179.99')} billed yearly`,
      badge: hasSaving ? `Save ${saved}%` : null,
    },
    {
      interval: PAYMENT_INTERVAL.MONTHLY,
      plan: monthly,
      label: 'Monthly',
      price: money(monthly, false, '$24.99'),
      featuredNote: `${money(
        monthly,
        false,
        '$24.99',
      )} paid monthly after trial`,
      compactNote: 'Pay monthly, cancel anytime',
    },
  ];

  const selectPlan = plan => {
    if (!plan) return;
    dispatch(setSelectedPlan(plan));
    setItem('selectedPlan', JSON.stringify(plan));
  };

  return (
    <div className="ftl-plans">
      {plans.map(item =>
        item.interval === selectedInterval ? (
          <div className="ftl-plan ftl-plan--featured" key={item.interval}>
            <div className="ftl-plan__head">
              <span className="ftl-plan__label">{item.label}</span>
              {item.badge && (
                <span className="ftl-plan__badge">{item.badge}</span>
              )}
            </div>
            <div className="ftl-stack-24">
              <div className="ftl-plan__price">
                <span className="ftl-plan__amount">{item.price}</span>
                <span className="ftl-plan__per">/ month</span>
              </div>
              <p className="ftl-plan__note">{item.featuredNote}</p>
            </div>
            <CtaButton
              label="Start My Free Trial"
              onStart={() => onStart('Your free week — pricing')}
              loading={loading}
              className="ftl-btn--block ftl-btn--compact"
            />
          </div>
        ) : (
          <button
            type="button"
            className="ftl-plan ftl-plan--compact"
            key={item.interval}
            onClick={() => selectPlan(item.plan)}
            disabled={!item.plan || (isDisabled && loading)}
          >
            <span className="ftl-stack-4">
              <span className="ftl-h4">{item.label}</span>
              <span className="ftl-text-md ftl-plan__sub">
                {item.compactNote}
              </span>
            </span>
            <span className="ftl-plan__price ftl-plan__price--sm">
              <span className="ftl-h4">{item.price}</span>
              <span className="ftl-plan__per">/ month</span>
            </span>
          </button>
        ),
      )}
      <p className="ftl-text-md text-white text-center mb-0">
        Cancel any time from the payments section of your profile.
      </p>
    </div>
  );
};

PricingPlans.propTypes = sectionProps;

export const FreeWeek = ({ onStart, loading }) => (
  <section className="ftl-section ftl-week">
    <div className="ftl-container">
      <div className="ftl-week__box">
        <div className="ftl-week__bg" aria-hidden="true">
          <img src={FT_IMAGES.timelineCircles} alt="" />
        </div>
        <div className="ftl-week__row">
          <div className="ftl-week__intro">
            <div className="ftl-stack-24 text-white">
              <div className="ftl-stack-8">
                <span className="ftl-eyebrow ftl-eyebrow--sm text-white">
                  Your free week
                </span>
                <h2 className="ftl-h1 ftl-week__title">
                  Here&apos;s the timeline.
                </h2>
              </div>
              <p className="ftl-text-xl">
                You&apos;ll be able to cancel the subscription easily from the
                payments section under your profile.
              </p>
            </div>
            <ol className="ftl-timeline">
              {TIMELINE.map(step => (
                <li className="ftl-timeline__item" key={step.day}>
                  <span
                    className={clsx(
                      'ftl-timeline__day',
                      step.highlight && 'ftl-teal',
                    )}
                  >
                    {step.day}
                  </span>
                  <span className="ftl-stack-4">
                    <span className="ftl-h6">{step.title}</span>
                    <span className="ftl-text-md ftl-faint">{step.text}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <PhoneMockup
            className="ftl-week__phone"
            screen={FT_IMAGES.timelinePhoneScreen}
            video="https://assets.jen.health/jen-health-scroll.mp4"
            alt="Jen Health app — Discover"
          />
          <PricingPlans onStart={onStart} loading={loading} />
        </div>
      </div>
    </div>
  </section>
);

FreeWeek.propTypes = sectionProps;

/* ------------------------------------------------------------ 8 reasons */

const REASONS = [
  {
    title: 'Real expert guidance, without leaving your living room',
    text: 'No waitlist. No driving across town for a 15-minute appointment. No scheduling around a clinic’s hours. Every plan inside Jen Health is built by Doctors of Physical Therapy and delivered straight to your phone, laptop or TV, so you can start today instead of three weeks from now.',
  },
  {
    title: 'Stop googling “best stretch for…” at 11pm',
    text: 'One video says stretch it. The next says strengthen it. A third says foam roall it. Searching for pain relief online gets you a hundred conflicting answers and zero plan. Jen Health replaces the scroll with a single, structured program built for your exact pain point or movement goal so you stop collecting random tips and start following an actual plan.',
  },
  {
    title: 'Your life is full enough. Let us do the thinking.',
    text: 'No waitlist. No driving across town for a 15-minute appointment. No scheduling around a clinic’s hours. Every plan inside Jen Health is built by Doctors of Physical Therapy and delivered straight to your phone, laptop or TV, so you can start today instead of three weeks from now.',
  },
  {
    title: 'A roadmap built around your goals, not a generic program',
    text: 'Tell us where it hurts, what you’re working with, and what you want to get back to - running, picking up kids, sleeping through the night without your hip waking you up. Your Jen Health Roadmap lays out exactly where to start and what comes next, so you’re never wondering if you’re doing the right thing.',
  },
  {
    title: '5 to 15 minutes a day. That’s the whole commitment.',
    text: 'Developing real mobility, stability and coordination doesn’t come from marathon stretch sessions - they come from more efficient exercises done right and done consistently. Every Jen Health session is built to actually fit into a real day, whether that’s alongside your current workout schedule, your PT session or your Jen Health workout, so showing up becomes the easy part.',
  },
  {
    title: 'Those “Quick Fixes” will keep the pain coming back',
    text: 'A crack, a massage, a device that promises to “break up” scar tissue - they can feel good in the moment, but they don’t address why pain showed up in the first place. Jen Health is built on progressive, evidence-based programming so you’ll understand what’s actually going on in your body and build the strength and mobility to keep the pain from creeping back in.',
  },
  {
    title: 'Exercises that meet your body where it’s at',
    text: 'Every video walks you through proper form, why the exercise matters for your specific goal, and how to adjust it if something doesn’t feel right for your body. No more guessing if you’re doing it “right.” You’ll finally understand what you’re doing, why you’re doing it, and how to progress to get stronger.',
  },
  {
    title: 'Trusted by thousands who’ve already made the switch',
    text: 'You don’t have to take our word for it. Real members are ditching the guesswork, the wasted co-pays, and the cycle of pain and telling us they’re never going back to how they used to manage it. See what they’re saying below.',
  },
];

const MEMBER_STORIES = [
  {
    quote:
      '“My back pain has subsided and I’m sleeping without waking up. I’m so motivated to learn more and breathe.”',
    name: 'Virgie Hatley',
    since: 'Member since 2023',
    image: IMAGE_CONSTANT.Avatar1,
    imageWebP: IMAGE_CONSTANT.Avatar1WebP,
    tint: '#06bab2',
  },
  {
    quote:
      '“My run times, distance and recovery are definitely better since starting the program. Jen has improved my life so much.”',
    name: 'Joe Sanchez',
    since: 'Member since 2022',
    image: IMAGE_CONSTANT.Avatar3,
    imageWebP: IMAGE_CONSTANT.Avatar3WebP,
    tint: '#70a6ff',
  },
  {
    quote:
      '“I have to say that lower extremity flow unlocked more than just my hips. Transformational.”',
    name: 'Lynlee Copenhaver',
    since: 'Member since 2024',
    image: IMAGE_CONSTANT.Avatar2,
    imageWebP: IMAGE_CONSTANT.Avatar2WebP,
    tint: '#ffa17a',
  },
];

const Avatar = ({ image, imageWebP, tint, name }) => (
  <span className="ftl-avatar" style={{ backgroundColor: tint }}>
    <picture>
      {imageWebP && <source srcSet={imageWebP} type="image/webp" />}
      <img src={image} alt={name} width="48" height="48" />
    </picture>
  </span>
);

Avatar.propTypes = {
  image: PropTypes.string.isRequired,
  imageWebP: PropTypes.string,
  tint: PropTypes.string,
  name: PropTypes.string.isRequired,
};

export const Reasons = () => {
  const storiesRef = useCenteredScroll(1);
  const { listRef: reasonsListRef, scrollerRef: reasonsScrollerRef } =
    useReasonsLineProgress();

  return (
    <section className="ftl-section ftl-reasons">
      <div className="ftl-container">
        <div className="ftl-reasons__box">
          <div className="ftl-reasons__head">
            <h2 className="ftl-h1 ftl-navy">
              8 reasons it’s finally your year to ditch the pain and move with
              confidence
            </h2>
            <p className="ftl-text-xl ftl-muted">
              Here’s exactly why thousands of people are making this the year
              they stop managing pain and start moving through it
            </p>
          </div>
          <div className="ftl-reasons__scroller" ref={reasonsScrollerRef}>
            <ol className="ftl-reasons__list" ref={reasonsListRef}>
              {REASONS.map((reason, index) => {
                const isLeft = index % 2 === 1;
                const number = String(index + 1).padStart(2, '0');
                return (
                  <li
                    className={clsx(
                      'ftl-reason',
                      isLeft ? 'ftl-reason--left' : 'ftl-reason--right',
                    )}
                    key={reason.title}
                  >
                    <div className="ftl-reason__card">
                      <div className="ftl-stack-8">
                        <span className="ftl-eyebrow ftl-eyebrow--sm">
                          Reason {index + 1}
                        </span>
                        <h3 className="ftl-h4">{reason.title}</h3>
                      </div>
                      <p className="ftl-text-lg ftl-muted">{reason.text}</p>
                    </div>
                    <span className="ftl-reason__marker" aria-hidden="true">
                      <span>{number}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>

        <div className="ftl-stories">
          <h2 className="ftl-display">People who stopped waiting</h2>
          <div className="ftl-stories__grid" ref={storiesRef}>
            {MEMBER_STORIES.map(story => (
              <figure
                className="ftl-quote-card"
                key={story.name}
                data-ftl-slide
              >
                <div className="ftl-stack-24">
                  <img src={FT_IMAGES.quote} alt="" width="40" height="30" />
                  <blockquote className="ftl-h4">{story.quote}</blockquote>
                </div>
                <figcaption className="ftl-author">
                  <Avatar {...story} />
                  <span>
                    <span className="ftl-author__name">{story.name}</span>
                    <span className="ftl-author__meta">{story.since}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------- Video + plans by area */

const BODY_AREAS = [
  { name: 'Elbow', icon: IMAGE_CONSTANT.elbowIcon },
  { name: 'Full Body', icon: IMAGE_CONSTANT.FullbodyIcon },
  { name: 'Neck', icon: IMAGE_CONSTANT.neckIcon },
  { name: 'Shoulders', icon: IMAGE_CONSTANT.shouldersIcon },
  { name: 'Upper Back', icon: IMAGE_CONSTANT.upperBackIcon },
  { name: 'Wrist', icon: IMAGE_CONSTANT.wristIcon },
  { name: 'Ankle', icon: IMAGE_CONSTANT.ankleIcon },
  { name: 'Core', icon: IMAGE_CONSTANT.CoreIcon },
  { name: 'Groin', icon: IMAGE_CONSTANT.GroinIcon },
  { name: 'Hamstring', icon: IMAGE_CONSTANT.HamstringIcon },
  { name: 'Head', icon: IMAGE_CONSTANT.headIcon },
  { name: 'Hips', icon: IMAGE_CONSTANT.HipsIcon },
  { name: 'Knees', icon: IMAGE_CONSTANT.kneesIcon },
  { name: 'Lower Back', icon: IMAGE_CONSTANT.lowerBackIcon },
  { name: 'Pelvis', icon: IMAGE_CONSTANT.pelvisIcon },
  { name: 'Shin', icon: IMAGE_CONSTANT.shinIcon },
  { name: 'IT Band', icon: IMAGE_CONSTANT.itBandIcon },
];

export const Programs = ({ onStart, loading }) => {
  const [playing, setPlaying] = useState(false);
  const areasRef = useHorizontalWheelScroll();

  return (
    <>
      <section className="ftl-section ftl-video">
        <div className="ftl-container">
          <div className="ftl-video__media">
            {playing ? (
              <video
                className="ftl-video__player"
                controls
                autoPlay
                playsInline
                poster={FT_IMAGES.videoPoster}
              >
                <source
                  src={VIDEO_CONSTANT.bannerVideoWebm}
                  type="video/webm"
                />
                <source src={VIDEO_CONSTANT.bannerVideoMp4} type="video/mp4" />
              </video>
            ) : (
              <>
                <img
                  className="ftl-video__poster"
                  src={FT_IMAGES.videoPoster}
                  alt=""
                />
                <div className="ftl-video__fade" />
                <button
                  type="button"
                  className="ftl-video__play"
                  onClick={() => setPlaying(true)}
                  aria-label="Play video"
                >
                  <img src={FT_IMAGES.playButton} alt="" />
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="ftl-programs">
        <div className="ftl-container ftl-programs__intro">
          <h2 className="ftl-h2 ftl-programs__lead">
            Step-by-step exercise programs designed for you to spend only 15
            minutes a day
          </h2>
          <PhoneMockup
            className="ftl-programs__phone"
            screen={FT_IMAGES.stepsPhoneScreen}
            alt="Jen Health app — Low Back & SI Plan"
          />
          <p className="ftl-text-xl ftl-programs__aside">
            Move through mobility, deep core understanding, and build proper
            stability and strength through exercises that target the often
            missed areas and address the body as a whole, not just a painful
            area.
          </p>
        </div>

        <div className="ftl-programs__stack">
          <div className="ftl-container ftl-programs__body">
            <div className="ftl-programs__head">
              <h2 className="ftl-h1">
                Plans for the areas that hurt or full workouts to get you
                stronger
              </h2>
              <p className="ftl-text-xl">
                For the pain that&apos;s been sitting in your back, hips, knees
                or shoulders for months, or years, with no clear plan to make it
                better. For the person ready to return to workouts without the
                fear of re-injury. For the stiffness that&apos;s crept in over
                time, causing bending, squatting, or reaching to cost you the
                next day.
              </p>
            </div>
          </div>
          <div className="ftl-areas-viewport" ref={areasRef}>
            <div className="ftl-areas" role="list">
              <div className="ftl-areas__inset" aria-hidden="true" />
              {BODY_AREAS.map(area => (
                <div className="ftl-area" role="listitem" key={area.name}>
                  <img src={area.icon} alt="" width="160" height="160" />
                  <span className="ftl-area__name">{area.name}</span>
                </div>
              ))}
              <div
                className="ftl-areas__inset ftl-areas__inset--end"
                aria-hidden="true"
              />
            </div>
          </div>
          <div className="ftl-container ftl-programs__body">
            <CtaButton
              label="Start My Free Trial"
              onStart={() => onStart('Plans for the areas that hurt')}
              loading={loading}
              withArrow
            />
          </div>
        </div>
      </section>
    </>
  );
};

Programs.propTypes = sectionProps;

/* ------------------------------------------- Meet your guide + doctors */

const DOCTORS = [
  {
    quote: TEXT_CONSTANT.TESTIMONIAL_1,
    name: 'Dr. Jolene Brighten',
    title: 'NMD, FABNE',
    image: IMAGE_CONSTANT.Doctor1,
    imageWebP: IMAGE_CONSTANT.Doctor1WebP,
  },
  {
    quote: TEXT_CONSTANT.TESTIMONIAL_2,
    name: 'Dr. Shante Cofield',
    title: 'PT, DPT, OCS, CSCS, CF-L1',
    image: IMAGE_CONSTANT.Doctor2,
    imageWebP: IMAGE_CONSTANT.Doctor2WebP,
  },
  {
    quote: TEXT_CONSTANT.TESTIMONIAL_3,
    name: 'Dr. Breanna Welsh',
    title: 'PT, DPT',
    image: IMAGE_CONSTANT.Doctor3,
    imageWebP: IMAGE_CONSTANT.Doctor3WebP,
  },
  {
    quote: TEXT_CONSTANT.TESTIMONIAL_4,
    name: 'Dr. Ayonna Procter',
    title: 'PT, DPT',
    image: IMAGE_CONSTANT.Doctor4,
    imageWebP: IMAGE_CONSTANT.Doctor4WebP,
  },
  {
    quote: TEXT_CONSTANT.TESTIMONIAL_5,
    name: 'Dr Jessica Greene',
    title: 'ND, DC',
    image: IMAGE_CONSTANT.Doctor5,
    imageWebP: IMAGE_CONSTANT.Doctor5WebP,
  },
];

export const Guide = () => {
  const [active, setActive] = useState(2);
  const shift = (DOCTORS.length - 1) / 2 - active;
  // Mobile: the track becomes a native swipe row, opened on the middle card.
  const doctorsRef = useCenteredScroll(2);

  return (
    <section className="ftl-guide">
      <div className="ftl-container ftl-container--wide ftl-guide__row">
        <div className="ftl-photo-card ftl-guide__photo">
          <img
            className="ftl-photo-card__img"
            src={FT_IMAGES.guidePhoto}
            alt="Dr. Jen Fraboni"
          />
          <div className="ftl-photo-card__caption ftl-stack-16">
            <h3 className="ftl-h2 text-white">Dr. Jen Fraboni</h3>
            <p className="ftl-text-xl text-white ftl-guide__role">
              PT, DPT, Doctor of physical therapy, founder of Jen Health
            </p>
          </div>
        </div>
        <div className="ftl-guide__story">
          <h2 className="ftl-h1">Meet your guide</h2>
          <div className="ftl-text-xl ftl-muted">
            <p>
              For years, I watched people cycle through the same frustrating
              loop: get a diagnosis, do a few weeks of in-person PT, get
              discharged with a photocopied sheet of exercises, and lose all
              momentum within a month. Or worse — get scared off exercise
              entirely after an injury, and just stop moving.
            </p>
            <p>
              I built Jen Health because real physical therapy shouldn&apos;t
              end when your appointments run out. You deserve a plan that
              evolves with you, teaches you the &quot;why&quot; behind every
              movement, and meets you exactly where your body is today — not
              where it used to be.
              <br />
              This isn&apos;t a generic workout app. It&apos;s the same clinical
              thinking I&apos;ve used with patients for years, made accessible
              every single day, wherever you are.
            </p>
          </div>
        </div>
      </div>

      <div className="ftl-doctors">
        <h2 className="ftl-display">Recommended by leading doctors</h2>
        <div className="ftl-doctors__viewport" ref={doctorsRef}>
          <div className="ftl-doctors__track" style={{ '--ftl-shift': shift }}>
            {DOCTORS.map((doctor, index) => (
              <button
                type="button"
                key={doctor.name}
                className={clsx(
                  'ftl-doctor',
                  index === active && 'is-active',
                  Math.abs(index - active) % 2 === 1 && 'is-lowered',
                )}
                onClick={() => setActive(index)}
                aria-pressed={index === active}
                data-ftl-slide
              >
                <span className="ftl-stack-24">
                  <img src={FT_IMAGES.quote} alt="" width="40" height="30" />
                  <span className="ftl-h5">{doctor.quote}</span>
                </span>
                <span className="ftl-author">
                  <Avatar
                    image={doctor.image}
                    imageWebP={doctor.imageWebP}
                    name={doctor.name}
                  />
                  <span>
                    <span className="ftl-author__name">{doctor.name}</span>
                    <span className="ftl-author__meta">{doctor.title}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------- Final CTA */

export const FinalCta = ({ onStart, loading }) => (
  <section className="ftl-final">
    <img className="ftl-final__bg" src={FT_IMAGES.ctaCircles} alt="" />
    <div className="ftl-final__content">
      <div className="ftl-stack-16 align-items-center">
        <Pill light>your year starts today</Pill>
        <h2 className="ftl-h1 text-white text-center">
          Stop waiting for the pain to go away on it&apos;s own
        </h2>
      </div>
      <p className="ftl-text-xl text-white text-center">
        Start your free trial and get your first follow-along session today. No
        clinic visit, no equipment required, no long-term commitment to try it.
      </p>
      <CtaButton
        label="Start my free week"
        onStart={() => onStart('Your year starts today')}
        loading={loading}
        variant="white"
      />
    </div>
  </section>
);

FinalCta.propTypes = sectionProps;
